// Klemento API client. v1.2.0 unified: 18-type taxonomy, Mercury-VP coaching
// (behaviour metrics + practice focus + score delta), trends + calendar +
// trajectory endpoints, /v1/users/me profile, /v1/focus-templates, MCP
// connector flows for Granola + Fathom.

const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? "https://api.klemento.com";

// ---------------------------------------------------------------------------
// Call type taxonomy (18 leaf types in 6 categories)

export type CallType =
  | "one_on_one_regular"
  | "one_on_one_skip_level"
  | "one_on_one_career_or_review"
  | "one_on_one_difficult_feedback"
  | "one_on_one_mentor"
  | "sales_discovery"
  | "sales_demo"
  | "sales_negotiation_or_pricing"
  | "sales_closing"
  | "sales_post_sale"
  | "hiring_interview"
  | "hiring_debrief"
  | "team_standup"
  | "team_working"
  | "team_all_hands"
  | "leadership_internal"
  | "leadership_investor"
  | "external_speaking";

export const CALL_TYPE_LABELS: Record<CallType, string> = {
  one_on_one_regular: "Regular 1:1",
  one_on_one_skip_level: "Skip-level 1:1",
  one_on_one_career_or_review: "Career or review 1:1",
  one_on_one_difficult_feedback: "Difficult feedback",
  one_on_one_mentor: "Mentor 1:1",
  sales_discovery: "Discovery call",
  sales_demo: "Demo",
  sales_negotiation_or_pricing: "Pricing or negotiation",
  sales_closing: "Closing call",
  sales_post_sale: "Post-sale (kickoff / QBR / renewal)",
  hiring_interview: "Interview",
  hiring_debrief: "Hiring debrief",
  team_standup: "Standup",
  team_working: "Working session",
  team_all_hands: "All-hands",
  leadership_internal: "Leadership (internal)",
  leadership_investor: "Investor pitch",
  external_speaking: "External speaking",
};

export type CallCategory = "one_on_one" | "sales" | "hiring" | "team" | "leadership" | "external" | "general";

export function callTypeToCategory(t: CallType): Exclude<CallCategory, "general"> {
  if (t.startsWith("one_on_one_")) return "one_on_one";
  if (t.startsWith("sales_")) return "sales";
  if (t.startsWith("hiring_")) return "hiring";
  if (t.startsWith("team_")) return "team";
  if (t.startsWith("leadership_")) return "leadership";
  return "external";
}

// ---------------------------------------------------------------------------
// Dimensions, scores, and behaviour metrics

export type Dimension = "clarity" | "confidence" | "conciseness" | "listening" | "structure" | "pacing";
export type DimensionScore = { score: number; weight: number; label: string };
export type ScoreDelta = Record<Dimension, number>;

export type BehaviourName =
  | "filler_words"
  | "position_weighted_hedges"
  | "weak_openers"
  | "premature_agreement"
  | "speaking_pace"
  | "talk_time_ratio"
  | "longest_monologue"
  | "patience"
  | "interruptions"
  | "question_quality"
  | "active_listening"
  | "specificity"
  | "confidence_markers"
  | "commitments";

export type BehaviourSeverity = "ok" | "low" | "medium" | "high";

export type BehaviourMetric = {
  name: BehaviourName;
  count: number;
  threshold: string;
  severity: BehaviourSeverity;
  evidence: Array<{ timestamp: string; quote: string }>;
};

export const BEHAVIOUR_LABELS: Record<BehaviourName, string> = {
  filler_words: "Filler words",
  position_weighted_hedges: "Hedge phrases",
  weak_openers: "Weak openers",
  premature_agreement: "Premature agreement",
  speaking_pace: "Speaking pace",
  talk_time_ratio: "Talk-time ratio",
  longest_monologue: "Longest monologue",
  patience: "Listening patience",
  interruptions: "Interruptions",
  question_quality: "Question quality",
  active_listening: "Active listening",
  specificity: "Specificity",
  confidence_markers: "Confidence markers",
  commitments: "Commitments",
};

// ---------------------------------------------------------------------------
// Practice focus

export type FocusStatus = "showed_up" | "absent" | "improved" | "slipped";

export type PracticeFocusCheck = {
  focus_id: string;
  status: FocusStatus;
  evidence?: { timestamp: string; quote: string } | null;
  suggestion: string;
};

export type PracticeFocusItem = {
  id: string;
  label: string;
  createdAt?: number; // server-stamped on create
};

export type FocusTemplate = {
  id: string;
  label: string;
  categories: CallCategory[];
};

// ---------------------------------------------------------------------------
// User profile

export type CoachingStyle = "direct" | "balanced" | "detailed" | "gentle";

export type UserProfile = {
  id: string;
  email: string;
  name: string | null;
  plan: "free" | "pro" | "team";
  coachingStyle: CoachingStyle;
  practiceFocus: PracticeFocusItem[];
};

// Alias kept for any existing callsites that import `Me` from this module.
export type Me = UserProfile;

// ---------------------------------------------------------------------------
// Analysis results, list items, trends

export type AnalysisMoment = {
  quote: string;
  timestamp?: string; // optional for backward-compat with v1.x rows
  label: "strong" | "missed" | "recovery" | "to_watch";
  coachingNote: string;
};

export type ReadingSource = "paste" | "granola" | "fathom";

export type AnalysisResult = {
  id: string;
  callType: CallType;
  createdAt: number;
  oneLineReadout: string;
  whatWorked: string;
  growthEdge: string;
  practiceAction: string;
  scores: Record<Dimension, DimensionScore>;
  overallScore: number | null;
  moments: AnalysisMoment[];
  behaviourMetrics: BehaviourMetric[];
  practiceFocusChecks: PracticeFocusCheck[];
  scoreDelta: ScoreDelta | null;
  source?: ReadingSource;
  sourceMeetingId?: string | null;
  meetingTitle?: string | null;
  meetingDate?: number | null;
};

export type AnalysisListItem = {
  id: string;
  callType: CallType;
  oneLineReadout: string;
  createdAt: number;
  source: ReadingSource;
  meetingTitle: string | null;
  meetingDate: number | null;
  overallScore: number | null;
};

export type TrendItem = {
  id: string;
  callType: CallType;
  createdAt: number;
  overallScore: number | null;
  scoreDelta: ScoreDelta | null;
  oneLineReadout: string;
};

// Calendar bucketing for a date range. Server returns groups sorted by date
// descending. `from` and `to` are unix seconds, [from, to).
export type CalendarItem = {
  id: string;
  callType: CallType;
  meetingTitle: string | null;
  overallScore: number | null;
};

export type TrendDimension = {
  name: Dimension;
  average: number | null;
  priorAverage: number | null;
  deltaVsPrior: number | null;
  readingsCount: number;
};

export type TrendsResponse = {
  window: "7d" | "30d";
  windowStart: number;
  overall: {
    average: number | null;
    priorAverage: number | null;
    deltaVsPrior: number | null;
  };
  readingsCount: number;
  priorReadingsCount: number;
  dimensions: TrendDimension[];
};

// ---------------------------------------------------------------------------
// Patterns surface (Tier 2 cross-meeting moat)

export type BehaviourTrendLabel = "rising" | "falling" | "stable";

export type BehaviourPattern = {
  name: BehaviourName;
  meetings: number;
  totalCount: number;
  averageCount: number;
  highCount: number;
  mediumCount: number;
  trendSlope: number;
  trendLabel: BehaviourTrendLabel;
  recentEvidence: Array<{ analysisId: string; createdAt: number; quote: string; timestamp: string }>;
};

export type DimensionPattern = {
  dimension: Dimension;
  averageScore: number;
  trajectory: Array<{ score: number; createdAt: number }>;
  trendSlope: number;
  trendLabel: BehaviourTrendLabel;
};

export type FocusArc = {
  focusId: string;
  label: string;
  timeline: Array<{ analysisId: string; createdAt: number; status: FocusStatus }>;
  showedUp: number;
  improved: number;
  slipped: number;
  absent: number;
  trendLabel: "improving" | "slipping" | "stable" | "absent";
};

export type RecentMeeting = {
  id: string;
  callType: CallType;
  createdAt: number;
  overallScore: number | null;
  overallScoreLabel: string | null;
  oneLineReadout: string;
};

export type PatternsResponse = {
  days: number;
  meetingCount: number;
  perBehaviour: BehaviourPattern[];
  perDimension: DimensionPattern[];
  focusArcs: FocusArc[];
  recentMeetings: RecentMeeting[];
};

// ---------------------------------------------------------------------------
// Connector types

export type ConnectorStatus = {
  connected: boolean;
  expiresAt?: number;
  expired?: boolean;
};

export type ConnectorsStatusResponse = {
  granola: ConnectorStatus;
  fathom: ConnectorStatus;
};

export type GranolaMeetingItem = {
  id: string;
  title: string;
  date?: string;
  rawDate?: string;
  attendees?: string[];
};

export type FathomMeetingItem = {
  id: string;
  title: string;
  date?: string;
  rawDate?: string;
  attendees?: string[];
};

export type SyncResponse = {
  added: number;
  skipped: number;
  errors: Array<{ provider: "granola" | "fathom"; meetingId: string; message: string }>;
};

// ---------------------------------------------------------------------------
// Reading counts. Used by the sidebar Library section badges. The endpoint
// is intentionally cheap (one COUNT query per dimension); falling back to
// `null` is safe — the sidebar just hides badges.

export type AnalysisCounts = {
  total: number;
  byCallType: Partial<Record<CallType, number>>;
  byCategory: Partial<Record<CallCategory, number>>;
};

// ---------------------------------------------------------------------------
// HTTP client

export class SessionExpiredError extends Error {
  constructor() {
    super("Your session has expired. Please sign in again.");
    this.name = "SessionExpiredError";
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    credentials: "include",
    headers: {
      ...(init?.headers ?? {}),
      ...(init?.body && !(init?.headers && "Content-Type" in init.headers) ? { "Content-Type": "application/json" } : {}),
    },
  });
  if (res.status === 401) {
    throw new SessionExpiredError();
  }
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: string } | null;
    throw new Error(body?.error ?? `Request failed: ${res.status}`);
  }
  return (await res.json()) as T;
}

// ---------------------------------------------------------------------------
// Endpoints

export function analyse(input: { transcript: string; callType: CallType }): Promise<AnalysisResult> {
  return request<AnalysisResult>("/v1/analyse", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function listAnalyses(args?: {
  limit?: number;
  before?: number;
  callType?: CallType;
  source?: ReadingSource;
}): Promise<{ items: AnalysisListItem[]; nextCursor: number | null }> {
  const params = new URLSearchParams();
  if (args?.limit) params.set("limit", String(args.limit));
  if (args?.before) params.set("before", String(args.before));
  if (args?.callType) params.set("callType", args.callType);
  if (args?.source) params.set("source", args.source);
  const qs = params.toString();
  return request(`/v1/analyses${qs ? `?${qs}` : ""}`);
}

export function getAnalysis(id: string): Promise<AnalysisResult> {
  return request<AnalysisResult>(`/v1/analyses/${encodeURIComponent(id)}`);
}

export function getCalendar(args: { from: number; to: number }): Promise<{
  days: Array<{ date: string; items: CalendarItem[] }>;
}> {
  const params = new URLSearchParams({ from: String(args.from), to: String(args.to) });
  return request(`/v1/analyses/calendar?${params.toString()}`);
}

export function getTrends(args?: { window?: "7d" | "30d" }): Promise<TrendsResponse> {
  const params = new URLSearchParams();
  if (args?.window) params.set("window", args.window);
  const qs = params.toString();
  return request(`/v1/trends${qs ? `?${qs}` : ""}`);
}

export function listTrends(args?: { limit?: number }): Promise<{ items: TrendItem[] }> {
  const params = new URLSearchParams();
  if (args?.limit) params.set("limit", String(args.limit));
  const qs = params.toString();
  return request(`/v1/analyses/trends${qs ? `?${qs}` : ""}`);
}

export function getPatterns(args?: { days?: number }): Promise<PatternsResponse> {
  const params = new URLSearchParams();
  if (args?.days) params.set("days", String(args.days));
  const qs = params.toString();
  return request(`/v1/patterns${qs ? `?${qs}` : ""}`);
}

export function getMe(): Promise<UserProfile> {
  return request<UserProfile>("/v1/users/me");
}

export function getAnalysisCounts(): Promise<AnalysisCounts> {
  return request<AnalysisCounts>("/v1/analyses/counts");
}

// Send {id?, label}[]; the server preserves ids on labels that haven't
// changed and stamps a fresh uuid + createdAt for new items. Empty array
// clears the user's practice focus.
export function updateMe(input: {
  coachingStyle?: CoachingStyle;
  practiceFocus?: Array<{ id?: string; label: string }>;
}): Promise<UserProfile> {
  return request<UserProfile>("/v1/users/me", {
    method: "PATCH",
    body: JSON.stringify(input),
  });
}

export function listFocusTemplates(args?: { categories?: CallCategory[] }): Promise<{ templates: FocusTemplate[] }> {
  const params = new URLSearchParams();
  if (args?.categories?.length) params.set("categories", args.categories.join(","));
  const qs = params.toString();
  return request(`/v1/focus-templates${qs ? `?${qs}` : ""}`);
}

// ---------------------------------------------------------------------------
// Connector flows

export function getConnectorStatus(): Promise<ConnectorsStatusResponse> {
  return request<ConnectorsStatusResponse>("/v1/connectors/status");
}

export function syncMeetings(): Promise<SyncResponse> {
  return request<SyncResponse>("/v1/connectors/sync", { method: "POST" });
}

export function startGranolaConnect(): Promise<{ authorizeUrl: string }> {
  return request<{ authorizeUrl: string }>("/v1/connectors/granola/connect", { method: "POST" });
}

export function testGranolaConnection(): Promise<unknown> {
  return request<unknown>("/v1/connectors/granola/test");
}

export function listGranolaMeetings(): Promise<{
  ok: boolean;
  items?: GranolaMeetingItem[];
  raw?: unknown;
  error?: string;
}> {
  return request<{ ok: boolean; items?: GranolaMeetingItem[]; raw?: unknown; error?: string }>(
    "/v1/connectors/granola/meetings",
  );
}

export function analyseGranolaMeeting(input: { meetingId: string; callType: CallType }): Promise<AnalysisResult> {
  return request<AnalysisResult>("/v1/connectors/granola/analyse", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function startFathomConnect(): Promise<{ authorizeUrl: string }> {
  return request<{ authorizeUrl: string }>("/v1/connectors/fathom/connect", { method: "POST" });
}

export function testFathomConnection(): Promise<unknown> {
  return request<unknown>("/v1/connectors/fathom/test");
}

export function listFathomMeetings(): Promise<{
  ok: boolean;
  items?: FathomMeetingItem[];
  raw?: unknown;
  error?: string;
}> {
  return request<{ ok: boolean; items?: FathomMeetingItem[]; raw?: unknown; error?: string }>(
    "/v1/connectors/fathom/meetings",
  );
}

export function analyseFathomMeeting(input: { meetingId: string; callType: CallType }): Promise<AnalysisResult> {
  return request<AnalysisResult>("/v1/connectors/fathom/analyse", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function health(): Promise<{ status: string; service: string; version: string }> {
  const res = await fetch(`${API_BASE}/health`);
  return (await res.json()) as { status: string; service: string; version: string };
}
